import { translateBackend } from '@assets/i18n/config';
import { FeatureFlagTypes } from '@generated/graphql';
import type { GoalTypes } from '@interfaces/goalEnums';
import { Badge } from '@ovComponents/2-component/badge/badge';
import { TextField } from '@ovComponents/2-component/textField/textField';
import { SelectionTile } from '@ovComponents/3-pattern/selectionTile/selectionTile';
import { WorkflowCompletionForm } from '@ovComponents/4-module/workflowCompletion/components/workflowCompletionForm';
import { AnalyticsContext } from '@providers/analyticsProvider';
import { useOrganizationFeatureFlag } from '@providers/userContextProvider';
import { ovAnalyticsEvents } from '@utils/analytics/analytics';
import { Typography } from 'hephaestus/web';
import { kebabCase } from 'lodash-es';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { type FieldOptions, type FormErrors, validateFields } from '../utils';
import type { GoalData, GoalsByType } from './createGoal';

export const CreateGoalVisual = ({
  options,
  goalData,
  loading,
  updateGoal,
  continueFunc,
  workflowCompletion,
  goalsByType = {},
  confirmationDialog,
  goalCreationMethodChoiceDialog,
}: {
  options: any;
  goalData: GoalData;
  loading: boolean;
  updateGoal: (g: GoalData) => void;
  continueFunc: () => void;
  workflowCompletion?: any;
  goalsByType?: GoalsByType;
  confirmationDialog?: React.ReactNode;
  goalCreationMethodChoiceDialog?: React.ReactNode;
}) => {
  const { sendAnalytic } = useContext(AnalyticsContext);
  const { t } = useTranslation(['client', 'workflowCompletions']);
  const goalCreationMethodEnabled = useOrganizationFeatureFlag(
    FeatureFlagTypes.GoalCreationMethod,
  );
  const [otherNameIsEmpty, setOtherNameIsEmpty] = useState<boolean>(false);
  const [focused, setFocused] = useState<string[]>([]);
  const [errors, setErrors] = useState<FormErrors>(null);
  const fieldOptions: FieldOptions = useMemo(
    () => ({
      type: { required: true },
    }),
    [],
  );
  const validate = useCallback(
    (candidateFields?: string[]): FormErrors => {
      const data = {
        type: goalData.type,
      };
      const newErrors = validateFields(fieldOptions, data, candidateFields);
      setErrors(newErrors);
      return newErrors;
    },
    [fieldOptions, goalData],
  );

  const goalConfig: Record<string, { value: string; label: string }> = {
    retirement: { value: 'RETIREMENT', label: t('goalType.RETIREMENT') },
    retirementIncome: {
      value: 'RETIREMENT_INCOME',
      label: t('goalType.RETIREMENT_INCOME'),
    },
    buildWealth: { value: 'BUILD_WEALTH', label: t('goalType.BUILD_WEALTH') },
    legacy: { value: 'LEGACY', label: t('goalType.LEGACY') },
    majorPurchase: {
      value: 'MAJOR_PURCHASE',
      label: t('goalType.MAJOR_PURCHASE'),
    },
    house: { value: 'HOUSE', label: t('goalType.HOUSE') },
    safetyNet: { value: 'SAFETY_NET', label: t('goalType.SAFETY_NET') },
    cashReserve: { value: 'CASH_RESERVE', label: t('goalType.CASH_RESERVE') },
    car: { value: 'CAR', label: t('goalType.CAR') },
    education: { value: 'EDUCATION', label: t('goalType.EDUCATION') },
    vacation: { value: 'VACATION', label: t('goalType.VACATION') },
    startABusiness: {
      value: 'START_A_BUSINESS',
      label: t('goalType.START_A_BUSINESS'),
    },
    wedding: { value: 'WEDDING', label: t('goalType.WEDDING') },
    group: { value: 'GROUP', label: t('goalType.GROUP') },
    parentalLeave: {
      value: 'PARENTAL_LEAVE',
      label: t('goalType.PARENTAL_LEAVE'),
    },
    other: { value: 'OTHER', label: t('goalType.OTHER') },
  };

  const submit = async () => {
    const otherHasNoName = goalData.type === 'OTHER' && !goalData?.name?.trim();
    setOtherNameIsEmpty(otherHasNoName);
    const formErrors = validate();

    if (formErrors) {
      setFocused(Object.keys(formErrors));
      return;
    }

    if (!formErrors && !otherHasNoName) {
      sendAnalytic(ovAnalyticsEvents.workflowsCreateGoalContinueButtonSelect, {
        workflowStepTitle: options?.title,
        workflowId: workflowCompletion?.workflow?.id,
        workflowName: workflowCompletion?.workflow?.name,
        activeWorkflowCompletionId: workflowCompletion?.id,
        objectId: workflowCompletion?.objectId,
        objectType: workflowCompletion?.objectType,
      });
      await continueFunc();
    }
  };

  useEffect(() => {
    validate(focused);
  }, [validate, focused]);

  if (!options) return <></>;

  const goalOptions: React.ComponentProps<
    typeof SelectionTile
  >['options'][number][] = Object.keys(goalConfig)
    .filter((key: string) => options[key] === true || options[key]?.enabled)
    .map((key: string) => {
      if (options[key].customLabelsTemp?.goalLabel.enabled) {
        const customLabel = translateBackend(
          options[key].customLabelsTemp.goalLabel,
        );
        return {
          value: goalConfig[key].value,
          label: customLabel !== '' ? customLabel : goalConfig[key].label,
        };
      }
      return goalConfig[key];
    })
    .map((o) => {
      const existingGoalCount = goalsByType[o.value as GoalTypes]?.length || 0;
      return {
        ...o,
        labelSideComponent:
          goalCreationMethodEnabled && existingGoalCount ? (
            <Badge
              label={t('workflowCompletions:createGoal.numGoalsBadge', {
                numGoals: existingGoalCount,
              })}
            />
          ) : undefined,
      };
    });

  const getGoalName = (type: GoalTypes) => {
    const goalOption = goalOptions.find((o) => o.value === type);
    return goalOption?.label ?? type;
  };

  return (
    <WorkflowCompletionForm
      onSubmit={submit}
      submitButtonProps={{
        label: t('continue'),
        disabled: loading,
        dataTestId: `create-goal-${kebabCase(translateBackend(options.title))}-continue-button`,
      }}
    >
      <Typography variant='displayLarge' sx={{ mt: 1 }}>
        {translateBackend(options?.title)}
      </Typography>
      <Typography variant='bodyLarge' sx={{ mb: 3 }}>
        <ReactMarkdown linkTarget='_blank' remarkPlugins={[remarkGfm]}>
          {translateBackend(options?.subtitle)}
        </ReactMarkdown>
      </Typography>

      <SelectionTile
        testId='create-goal-type'
        onChange={(e: any) =>
          updateGoal({
            type: e.target.value,
            name: getGoalName(e.target.value),
          })
        }
        value={goalData.type ?? ''}
        options={goalOptions}
        error={errors?.type?.active}
      />

      {goalData.type === 'OTHER' && (
        <TextField
          testId='create-goal-name'
          onChange={(e: any) =>
            updateGoal({ ...goalData, name: e.target.value.trimStart() })
          }
          disabled={loading}
          label={t('goalName')}
          sx={{ mt: 2 }}
          fullWidth
          onBlur={() => setOtherNameIsEmpty(false)}
          error={otherNameIsEmpty}
          value={goalData.name}
        />
      )}
      {confirmationDialog}
      {goalCreationMethodChoiceDialog}
    </WorkflowCompletionForm>
  );
};

export default CreateGoalVisual;
